import React from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  SkeletonBodyText,
} from '@shopify/polaris';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../services/api';
import { ComplianceChecklist } from '../components/ComplianceChecklist';
import { PageEntrance } from '../components/ui';
import './CompliancePage.css';

/**
 * T092: CompliancePage.
 * ComplianceChecklist, passed/total summary, all-green congratulatory state,
 * "unable to check" state for unreachable pages.
 */

interface ComplianceCheckResult {
  checkType: string;
  passed: boolean;
  severity: string;
  remediationText: string | null;
}

interface ComplianceReport {
  productId: string;
  productTitle: string;
  totalChecks: number;
  passedChecks: number;
  results: ComplianceCheckResult[];
}

function useComplianceReport(productId: string) {
  return useQuery<ComplianceReport>({
    queryKey: ['compliance', productId],
    queryFn: () => apiFetch(`/products/${productId}/compliance`),
    staleTime: 1000 * 60 * 5,
  });
}

export function CompliancePage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useComplianceReport(id ?? '');

  if (isLoading) {
    return (
      <Page
        title="Compliance Checks"
        backAction={{ onAction: () => navigate(-1) }}
      >
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={8} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page
        title="Compliance Checks"
        backAction={{ onAction: () => navigate(-1) }}
      >
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load compliance report. Please try again later.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const allUnreachable = data.passedChecks === 0 && data.results.every(
    (r) => r.remediationText?.includes('unreachable') || r.remediationText?.includes('password-protected'),
  );

  const allPassed = data.passedChecks === data.totalChecks;

  return (
    <PageEntrance>
    <Page
      title={`Compliance: ${data.productTitle}`}
      backAction={{ onAction: () => navigate(`/products/${data.productId}`) }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {/* Summary Card */}
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Summary</Text>
                <InlineStack gap="400" blockAlign="center">
                  <Text as="span" variant="headingXl">
                    {data.passedChecks}/{data.totalChecks}
                  </Text>
                  <Text as="span" variant="bodyMd" tone="subdued">
                    checks passed
                  </Text>
                  <Badge
                    tone={
                      allPassed ? 'success'
                        : data.passedChecks >= 7 ? 'warning'
                          : 'critical'
                    }
                  >
                    {allPassed ? 'All Passed'
                      : `${data.totalChecks - data.passedChecks} issues`}
                  </Badge>
                </InlineStack>
              </BlockStack>
            </Card>

            {/* All-green congratulatory state */}
            {allPassed && (
              <Banner tone="success">
                <p>
                  All compliance checks passed! Your product page follows SEO and
                  structured data best practices.
                </p>
              </Banner>
            )}

            {/* Unreachable state */}
            {allUnreachable && (
              <Banner tone="warning">
                <p>
                  Unable to check your product page. It may be password-protected
                  or temporarily unavailable. Please try again later.
                </p>
              </Banner>
            )}

            {/* Checklist */}
            <ComplianceChecklist results={data.results} />
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
    </PageEntrance>
  );
}
